import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Image from "next/image";
import Logo from "../Svg/Icons/Logo";

/**
 * Both prices in one 40 px chip, between the phone bar's 576 px and the full pills (1600 px): the two coins stacked, each with its
 * price ($0.00000026), so the numbers are readable at every desktop width from 1024 px (Bez 2026-10-07: since the Competitions tab
 * only the two coin icons showed below 1600 px). 968-1023 px has no room for the numbers next to six tabs: the icons only, as before.
 * Hover or tap opens a small popover with both prices; each row links to the swap, as the pills do. A click only opens (a mouse
 * click arrives after the hover already opened it); leaving, Escape or a press outside closes.
 */
const ICONS_ONLY = "@media screen and (min-width: 968px) and (max-width: 1023.98px)";

const Wrap = styled.div`
  position: relative;
  display: inline-flex;
`;
const Btn = styled.button`
  all: unset;
  box-sizing: border-box;
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  height: 40px;
  padding: 0 10px 0 7px;
  border-radius: 12px;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  background: linear-gradient(135deg, rgba(127, 212, 232, 0.08), rgba(126, 0, 254, 0.08));
  transition: border-color 140ms ease-out, box-shadow 140ms ease-out;
  &:hover,
  &[aria-expanded="true"] {
    border-color: rgba(127, 212, 232, 0.55);
    box-shadow: 0 0 12px -2px rgba(127, 212, 232, 0.35);
  }
  &:focus-visible {
    outline: 2px solid #7fd4e8;
    outline-offset: 2px;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
  ${ICONS_ONLY} {
    flex-direction: row;
    align-items: center;
    gap: 0;
    padding: 0 6px;
    border-color: transparent;
    background: none;
    box-shadow: none;
    > :nth-child(2) {
      margin-left: -8px;
    }
  }
`;
const Row = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 16px;
  /* the CADINU mark is an svg with an inline width: fit-content (its own aspect ratio), so it is sized by its height */
  svg.pc-icon {
    height: 16px;
  }
  img.pc-icon {
    flex: none;
    width: 16px;
    height: 16px;
  }
  ${ICONS_ONLY} {
    height: auto;
    svg.pc-icon {
      height: 30px;
    }
    img.pc-icon {
      width: 26px;
      height: 26px;
    }
  }
`;
const Price = styled.span`
  font-size: 13px;
  line-height: 16px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.01em;
  color: ${({ theme }) => theme.colors.text};
  white-space: nowrap;
  ${ICONS_ONLY} {
    display: none;
  }
`;
const Pop = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 30;
  min-width: 210px;
  padding: 8px;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.backgroundAlt};
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  box-shadow: ${({ theme }) => theme.shadows.level1};
  display: grid;
  gap: 2px;
  a {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 44px;
    padding: 0 10px;
    border-radius: 12px;
    color: ${({ theme }) => theme.colors.text};
    font-weight: 600;
    white-space: nowrap;
  }
  a:hover {
    background: ${({ theme }) => theme.colors.tertiary};
  }
  span.p {
    margin-left: auto;
    color: ${({ theme }) => theme.colors.textSubtle};
    font-variant-numeric: tabular-nums;
  }
`;

export interface PriceChipProps {
  cadinuPriceUsd?: number;
  cbonPriceUsd?: number;
  chainId: number;
}

const usd = (v: number) => `$${v.toFixed(8)}`;

const PriceChip: React.FC<PriceChipProps> = ({ cadinuPriceUsd, cbonPriceUsd, chainId }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const down = (e: MouseEvent | TouchEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const key = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", down);
    document.addEventListener("touchstart", down);
    window.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", down);
      document.removeEventListener("touchstart", down);
      window.removeEventListener("keydown", key);
    };
  }, [open]);
  if (!cadinuPriceUsd && !cbonPriceUsd) return null;
  const label = [cadinuPriceUsd ? `CADINU ${usd(cadinuPriceUsd)}` : "", cbonPriceUsd ? `CBON ${usd(cbonPriceUsd)}` : ""]
    .filter(Boolean)
    .join(", ");
  return (
    <Wrap ref={ref} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)} data-testid="price-chip">
      <Btn type="button" aria-label={`Prices: ${label}`} aria-expanded={open} onClick={() => setOpen(true)}>
        {cadinuPriceUsd ? (
          <Row>
            <Logo width="16px" height="16px" className="pc-icon" />
            <Price data-testid="price-chip-cadinu">{usd(cadinuPriceUsd)}</Price>
          </Row>
        ) : null}
        {cbonPriceUsd ? (
          <Row>
            <Image width={26} height={26} src="/cbon-logo.png" alt="" className="pc-icon" />
            <Price data-testid="price-chip-cbon">{usd(cbonPriceUsd)}</Price>
          </Row>
        ) : null}
      </Btn>
      {open && (
        <Pop role="dialog" aria-label="Prices" data-testid="price-chip-pop">
          {cadinuPriceUsd ? (
            <a href={`https://apps.cadinu.io/swap?outputCurrency=0x76e112203eF59D445452ef7556386dD2DF3Ed914&chainId=${chainId}`} target="_blank" rel="noreferrer">
              <Logo width="22px" /> CADINU <span className="p">{usd(cadinuPriceUsd)}</span>
            </a>
          ) : null}
          {cbonPriceUsd ? (
            <a href={`https://apps.cadinu.io/swap?outputCurrency=0x6e64fCF15Be3eB71C3d42AcF44D85bB119b2D98b&chainId=56&chain=${chainId}`} target="_blank" rel="noreferrer">
              <Image width={22} height={22} src="/cbon-logo.png" alt="" /> CBON <span className="p">{usd(cbonPriceUsd)}</span>
            </a>
          ) : null}
        </Pop>
      )}
    </Wrap>
  );
};

export default PriceChip;
